import React from "react";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Plane,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  Star,
} from "lucide-react";

const ExploreRedeme = () => {
  return (
    <>
      <Helmet>
        <title>Explore Reward Redemption Opportunities | PointMinted</title>

        <meta
          name="description"
          content="Explore estimated travel reward redemption opportunities, premium cabin upgrades, and smarter ways to maximize airline miles and credit card points across major US loyalty programs."
        />

        <meta
          name="keywords"
          content="reward redemption opportunities, travel rewards, airline miles value, credit card points value, Chase points redemption, Amex reward calculator"
        />

        <meta
          property="og:title"
          content="Explore Reward Redemption Opportunities"
        />

        <meta
          property="og:description"
          content="Discover smarter travel reward opportunities and estimated premium redemption values."
        />
      </Helmet>

      <section className="relative overflow-hidden bg-[#020817] min-h-screen py-24 px-6 lg:px-16">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-400/10 blur-3xl rounded-full animate-blob"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-400/10 blur-3xl rounded-full animate-blob"></div>
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

          <svg
            className="absolute inset-0 w-full h-full opacity-20"
            viewBox="0 0 1400 800"
            fill="none"
          >
            <path
              d="M120 520 C320 180, 900 120, 1220 340"
              stroke="#D4AF37"
              strokeWidth="2"
              strokeDasharray="10 10"
              className="animate-route"
            />
            <g className="animate-plane">
              <text x="0" y="0" fontSize="28">
                ✈️
              </text>
            </g>
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md text-[11px] font-black uppercase tracking-[0.2em] text-[#D4AF37] mb-8">
              <Sparkles size={14} />
              USA Reward Insights
            </div>

            <h1 className="text-5xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight">
              Explore Travel Reward
              <span className="block text-[#D4AF37]">
                Redemption Opportunities
              </span>
            </h1>

            <p className="text-slate-300 text-lg leading-relaxed mt-8 max-w-3xl">
              Discover estimated premium travel redemption opportunities,
              airline transfer insights, and smarter ways to maximize your
              reward points across major US loyalty programs.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mt-20">
            <div className="bg-white rounded-[2rem] p-6 shadow-[0_20px_80px_rgba(0,0,0,0.35)] border border-slate-100 hover:scale-[1.02] transition-all duration-300 animate-float">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-600 font-black">
                    Suggested Premium Redemption
                  </p>

                  <h3 className="text-2xl font-black text-slate-900 mt-1">
                    New York → London
                  </h3>
                </div>

                <div className="w-12 h-12 rounded-2xl bg-cyan-100 flex items-center justify-center">
                  <Plane size={20} className="text-cyan-700" />
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden h-52 relative">
                <img
                  src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=1200&auto=format&fit=crop"
                  alt="Travel"
                  className="w-full h-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>

                <div className="absolute bottom-5 left-5">
                  <p className="text-white text-sm font-bold">
                    Estimated Business Class Opportunity
                  </p>

                  <h2 className="text-4xl font-black text-[#D4AF37] mt-1">
                    Up to $1,284
                  </h2>

                  <p className="text-slate-300 text-xs mt-1">
                    Potential value using 82,000 reward points
                  </p>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-black text-emerald-600">
                    HIGH ESTIMATED VALUE
                  </p>

                  <p className="text-xs text-slate-400 mt-1">
                    Based on recent travel pricing trends
                  </p>
                </div>

                <div className="w-12 h-12 rounded-2xl bg-emerald-500 text-white flex items-center justify-center font-black">
                  92
                </div>
              </div>
            </div>

            <div className="bg-[#0f172a] border border-white/10 backdrop-blur-xl rounded-[2rem] p-6 hover:scale-[1.02] transition-all duration-300">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-red-400 font-black mb-5">
                <TrendingUp size={12} />
                Potentially Lower Redemption Value
              </div>

              <div className="flex items-end gap-3">
                <h2 className="text-4xl font-black text-white">$320</h2>

                <p className="text-red-400 text-sm font-bold mb-1">
                  Lower Estimated Value
                </p>
              </div>

              <p className="text-slate-400 text-sm mt-4 leading-relaxed">
                Some cashback and gift card redemptions may provide lower
                estimated value compared to premium travel reward opportunities.
              </p>

              <div className="mt-8">
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full w-[35%] bg-red-500 rounded-full"></div>
                </div>

                <div className="flex justify-between mt-2 text-[11px] font-bold text-slate-500">
                  <span>Estimated Value</span>
                  <span>Lower Return Potential</span>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-[2rem] p-6">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-black mb-5">
                <Star size={12} />
                Reward Insight
              </div>

              <h3 className="text-2xl font-black text-white">
                Compare Airline Transfer Partners
              </h3>

              <p className="text-slate-400 text-sm mt-4 leading-relaxed">
                Transfer partner availability and pricing may impact estimated
                reward value. Explore multiple travel programs to identify
                potentially stronger redemption opportunities.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/5">
                  <span className="text-sm font-bold text-white">
                    Premium Cabin Flights
                  </span>

                  <span className="text-emerald-400 text-sm font-black">
                    Higher Value
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/5">
                  <span className="text-sm font-bold text-white">
                    Cashback Rewards
                  </span>

                  <span className="text-yellow-400 text-sm font-black">
                    Moderate Value
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/5">
                  <span className="text-sm font-bold text-white">
                    Gift Cards
                  </span>

                  <span className="text-red-400 text-sm font-black">
                    Lower Value
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-20 p-6 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={22} className="text-emerald-400" />
            </div>

            <div>
              <h3 className="text-white font-black text-sm">
                Transparent Reward Estimates
              </h3>

              <p className="text-slate-400 text-sm leading-relaxed mt-2">
                Redemption values shown are estimates based on publicly
                available travel pricing, historical reward trends, and transfer
                partner availability. Actual values may vary depending on
                airline inventory, booking dates, and loyalty program changes.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ExploreRedeme;
